/**
 * The Daily Run (docs/spec/03 §6): today's UTC date and run number, what it is, the player's best
 * of the day, and PLAY.
 */

import type { Day } from '../../game/daily';
import { dateKey } from '../../game/daily';
import type { DailyBest } from '../../platform/scoreBook';
import type { Screen } from '../../render/screen';
import { drawCapsule } from '../../render/sprites';
import { CAPSULE_WEIGHTS } from '../../sim/tuning';
import { UiScene } from '../kit/base';
import { drawPanel } from '../kit/chrome';
import type { MenuLook } from '../kit/menu';
import { drawMenu } from '../kit/menu';
import type { MenuItem, Rect } from '../kit/menuModel';
import { moveSelection, rowAt } from '../kit/menuModel';
import { longDate, wrap } from '../kit/text';
import { ACCENT, capsuleTicks, CX, HEADING, INK, MUTED, TEXT_COLS } from '../kit/theme';
import type { MenuAction, PointerInput, SceneHost } from '../scene';
import { BEST_COLS, bestLines, DAILY_ABOUT, dailyItems, runTitle } from './dailyModel';

const DATE_Y = 36;
const NOTE_Y = 58;
const ABOUT_Y = 76;
const PODS_Y = 110;
const PANEL: Rect = { x: 24, y: 126, w: 176, h: 64 };
const MENU: MenuLook = { top: 208, rowH: 16, x: 24, w: 176, cx: CX };

export class DailyScene extends UiScene {
  protected readonly heading: string;
  private readonly day: Day;
  private best: DailyBest | null = null;
  private items: MenuItem[] = [];
  private sel = 0;

  constructor(host: SceneHost) {
    super(host);
    this.day = host.services.today();
    this.heading = runTitle(this.day);
    this.refresh();
  }

  private refresh(): void {
    this.best = this.services.scores.dailyBest(dateKey(this.day));
    this.items = dailyItems(this.best);
  }

  override enter(): void {
    this.refresh();
    super.enter();
  }

  protected override describe(): string {
    const b = this.best;
    const best = b ? `Your best today: ${b.score}, stage ${b.stage}, ${b.attempts} attempts.` : 'Not played yet today.';
    return `${this.heading}. ${DAILY_ABOUT} ${best} ${this.items[this.sel]?.label ?? ''}`;
  }

  protected override hint(): string {
    return `${this.words().ok} CHOOSE   ${this.words().back} BACK`;
  }

  protected paint(screen: Screen): void {
    const d = this.day;
    screen.text(longDate(d.year, d.month, d.day), CX, DATE_Y, ACCENT, 'center', 2);
    screen.text('THE DAY TURNS AT 00:00 UTC', CX, NOTE_Y, MUTED, 'center');
    wrap(DAILY_ABOUT, TEXT_COLS).forEach((line, i) => screen.text(line, CX, ABOUT_Y + i * 10, INK, 'center'));
    // Every capsule the day's seed will deal, bobbing in a row.
    const t = capsuleTicks(this.ms);
    CAPSULE_WEIGHTS.forEach(([kind], i) => {
      const x = CX + (i - (CAPSULE_WEIGHTS.length - 1) / 2) * 24;
      drawCapsule(screen, kind, x, PODS_Y + Math.round(Math.sin(this.ms / 400 + i) * 2), t);
    });
    drawPanel(screen, PANEL);
    screen.text('YOUR BEST TODAY', CX, PANEL.y + 8, HEADING, 'center');
    const lines = bestLines(this.best);
    const x = CX - (BEST_COLS * 8) / 2;
    if (lines.length === 1) screen.text(lines[0] ?? '', CX, PANEL.y + 34, MUTED, 'center');
    else lines.forEach((line, i) => screen.text(line, x, PANEL.y + 22 + i * 12, INK));
    drawMenu(screen, this.items, this.sel, MENU, this.ms);
  }

  private select(i: number): void {
    if (i === this.sel) return;
    this.sel = i;
    this.sound('move');
    this.say(this.items[i]?.label ?? '');
  }

  private choose(i: number): void {
    this.sel = i;
    if (this.items[i]?.id !== 'play') return this.back();
    this.sound('select');
    this.host.go({ to: 'play', request: { mode: 'daily' } });
  }

  protected onAction(a: MenuAction): void {
    if (a === 'up') this.select(moveSelection(this.items, this.sel, -1));
    else if (a === 'down') this.select(moveSelection(this.items, this.sel, 1));
    else if (a === 'confirm') this.choose(this.sel);
  }

  protected override onPointer(p: PointerInput): void {
    if (p.kind === 'wheel') return this.select(moveSelection(this.items, this.sel, p.delta));
    const i = rowAt(MENU, this.items.length, p.x, p.y);
    if (i < 0) return;
    if (p.kind === 'move') this.select(i);
    else if (p.kind === 'down' && p.button === 0) this.choose(i);
  }
}
